<template>
  <span v-if="disable" class="p-editable text-xs ellipsis text-neutral">
    {{ getRoundedOffValue(modelValue) }}
  </span>
  <div v-else>
    <slot name="help">
      <span v-if="helpMessage" class="text-neutral-70 text-xxs">
        {{ helpMessage }}
      </span>
    </slot>
    <div class="flex items-center relative-position">
      <span
        v-if="type === 'currency' && !disable"
        class="absolute q-py-xxs q-px-xs text-xs text-neutral"
      >
        ₹
      </span>
      <span
        class="p-editable outline text-xs ellipsis text-neutral col"
        contenteditable
        @blur="onBlur"
        @focus="setCaretToEndOfContentEditable($event.target)"
        @input="onInput"
        @keydown.enter.prevent
        @paste="onPaste"
      >
        {{ getRoundedOffValue(modelValue) }}
      </span>
    </div>
    <slot name="error">
      <span v-if="errorMessage" class="text-negative text-xxs">
        {{ errorMessage }}
      </span>
    </slot>
  </div>
</template>

<script setup>
import {
  parseLocaleNumber,
  roundOff,
  setCaretToEndOfContentEditable,
  numberFormat,
} from "assets/scripts/utilities/helpers";

const props = defineProps({
  disable: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: "",
  },
  helpMessage: {
    type: String,
    default: "",
  },
  max: {
    type: Number,
    default: Number.NaN,
  },
  mfd: {
    type: Number,
    default: 3,
  },
  min: {
    type: Number,
    default: 0,
  },
  modelValue: {
    type: null,
    required: true,
  },
  type: {
    type: String,
    default: "text",
  },
});

const computedStyles = computed(() => {
  return {
    padding:
      props.type === "currency" && !props.disable ? "6px 6px 6px 20px" : "6px",
  };
});

const emit = defineEmits(["update:model-value"]);

const isValidNumber = (value) => {
  return value?.trim?.() !== "" && !Number.isNaN(Number(value));
};

const validateMaxMin = (e, textContent) => {
  textContent = roundOff(textContent, props.mfd);

  if (isValidNumber(props.max) && textContent > props.max) {
    textContent = roundOff(props.max, props.mfd);

    e.target.textContent = textContent;
  }

  if (isValidNumber(props.min) && textContent < props.min) {
    textContent = roundOff(props.min, props.mfd);

    e.target.textContent = textContent;
  }

  return textContent;
};

const onBlur = (e) => {
  let textContent = e.target.textContent;

  if (isValidNumber(textContent)) {
    textContent = roundOff(textContent, props.mfd);
  } else {
    textContent = textContent.trim();
  }

  e.target.textContent = textContent;

  emit("update:model-value", textContent);
};

const onInput = (e) => {
  let textContent = e.target.textContent;

  if (["currency", "number"].includes(props.type)) {
    if (Number.isNaN(Number(textContent))) {
      textContent = props.modelValue;

      e.target.textContent = textContent;
    }

    if (isValidNumber(textContent)) {
      textContent = validateMaxMin(e, textContent);
    }
  }

  emit("update:model-value", textContent);

  nextTick(() => {
    setCaretToEndOfContentEditable(e.target);
  });
};

const onPaste = (e) => {
  e.preventDefault();

  let textContent = e.clipboardData.getData("text/plain");

  if (parseLocaleNumber(textContent)) {
    textContent = parseLocaleNumber(textContent);
  }

  if (["currency", "number"].includes(props.type)) {
    if (Number.isNaN(Number(textContent))) {
      textContent = props.modelValue;
    }

    if (isValidNumber(textContent)) {
      textContent = validateMaxMin(e, textContent);
    }
  }

  e.target.textContent = textContent;

  emit("update:model-value", textContent);

  nextTick(() => {
    setCaretToEndOfContentEditable(e.target);
  });
};

const getRoundedOffValue = (value) => {
  if (!isValidNumber(value)) {
    return value;
  } else if (props.disable && props.type === "currency") {
    return numberFormat({ number: value, style: "currency", mfd: props.mfd });
  } else {
    return roundOff(value, props.mfd);
  }
};
</script>

<style lang="scss" scoped>
.outline {
  background: $primary-20;
  border-radius: 8px;

  min-height: inherit;
  outline: 2px solid transparent;
  padding: v-bind("computedStyles.padding");
  transition: outline-color 0.36s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    outline-color: $primary-80;
  }
  &:focus-within {
    outline-color: $primary;
  }

  &[placeholder]:empty::before {
    content: attr(placeholder);
    color: $neutral-60;
  }

  &[placeholder]:empty:focus::before {
    content: "";
    display: none;
  }
}

.q-field {
  &--error {
    .outline {
      outline-color: $negative;
    }
  }
}

:global(.q-field--disabled:has(.p-editable) .q-field__control-container) {
  opacity: 1 !important;
}

:global(.q-field:has(.outline) .q-field__native) {
  min-height: 32px;
  padding: 0;
}

:global(.q-field:has(.outline) .q-field__bottom) {
  display: none;
  padding: 4px 0 0 0;
}
</style>
